import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTenant } from "@/lib/tenant";
import { ArrowLeft, Loader2, AlertTriangle, AlertCircle, Info, Tractor } from "lucide-react";

const SEV = {
  critical: { cls: "bg-red-500/15 text-red-300 border-red-500/40", icon: AlertTriangle },
  warning:  { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", icon: AlertCircle },
  info:     { cls: "bg-blue-500/15 text-blue-300 border-blue-500/40", icon: Info },
};

export default function FleetFaults() {
  const { api } = useTenant();
  const [faults, setFaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get(`/fieldops/faults?days=${days}`)
      .then(r => setFaults(r.data?.data || []))
      .finally(() => setLoading(false));
  }, [days]); // eslint-disable-line

  const filtered = faults.filter(f => {
    if (filter === "active") return f.active;
    if (filter === "critical") return f.severity === "critical";
    return true;
  });

  const counts = {
    total:    faults.length,
    active:   faults.filter(f => f.active).length,
    critical: faults.filter(f => f.severity === "critical").length,
  };

  return (
    <div className="p-8 max-w-7xl">
      <Link to="/admin/fleet" className="text-sm text-slate-400 hover:text-amber-300 inline-flex items-center gap-1 mb-1">
        <ArrowLeft size={14} /> Back to fleet
      </Link>
      <h1 className="text-2xl font-bold mb-1">Fleet faults</h1>
      <p className="text-sm text-slate-400 mb-5">Diagnostic codes (SPN/FMI) reported by your tractors over the selected window.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        {[
          { l: "All",      v: counts.total,    c: "text-slate-200" },
          { l: "Active",   v: counts.active,   c: "text-amber-300" },
          { l: "Critical", v: counts.critical, c: "text-red-300" },
        ].map(s => (
          <div key={s.l} className="bg-[#0a0e1c] border border-white/10 rounded-lg p-4 text-center">
            <div className={`text-3xl font-display font-black tabular-nums ${s.c}`}>{s.v}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="inline-flex bg-white/5 border border-white/10 rounded-lg p-1">
          {[["all","All"],["active","Active"],["critical","Critical"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
                    className={`px-3 py-1 rounded text-xs font-bold ${filter === k ? "bg-amber-500 text-[#0a0e1c]" : "text-slate-300"}`}>
              {l}
            </button>
          ))}
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
                className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm">
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : !filtered.length ? (
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-10 text-center text-slate-500">
          {faults.length === 0
            ? "No faults reported. Either everything is healthy, or no faults pushed to FieldOps yet."
            : "No faults match this filter."}
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((f, i) => {
            const s = SEV[f.severity] || SEV.info;
            const SIcon = s.icon;
            return (
              <li key={i} className={`bg-[#0a0e1c] border rounded-lg px-4 py-3 flex items-start gap-3 ${s.cls}`}>
                <SIcon size={18} className="shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold">{f.code || "—"}</span>
                    {f.fmi != null && <span className="text-[10px] font-mono text-slate-400">FMI {f.fmi}</span>}
                    <span className="text-[10px] uppercase tracking-widest font-bold">{f.severity}</span>
                    {f.active && <span className="text-[10px] uppercase tracking-widest font-bold bg-red-500/30 text-red-200 px-1.5 py-0.5 rounded">Active</span>}
                  </div>
                  <div className="text-sm text-slate-300 mt-1">{f.description || "(no description)"}</div>
                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                    <Link to={`/admin/fleet/${f.vin}`} className="inline-flex items-center gap-1 hover:text-amber-300">
                      <Tractor size={11} /> {f.machine || f.vin}
                    </Link>
                    {f.datetime && <span>{new Date(f.datetime).toLocaleString("en-GB")}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
